import { Tooltip } from "antd";
import {
    MessagesSquare,
    CircleDot,
    Mail,
    MailWarning,
    MailOpen,
    Star,
    MessageCircle,
    Phone,
    PhoneOff,
    UserX,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ChannelCustomerAdvancedFilters } from "../../api/ChannelCustomer";

/**
 * Thanh icon lọc nhanh bên trái danh sách khách hàng kênh.
 * Mỗi mục tương ứng đúng một bộ lọc API danh sách khách kênh đang hỗ trợ
 * (xem tài liệu, mục "Ví dụ thử nghiệm bộ lọc nâng cao").
 */
export type QuickFilterKey =
    | "all"
    | "unread"
    | "unanswered"
    | "unanswered_oldest"
    | "read_unanswered"
    | "priority"
    | "comment"
    | "has_phone"
    | "no_phone"
    | "unassigned";

type QuickFilterParams = ChannelCustomerAdvancedFilters & { type?: string };

export const QUICK_FILTERS: {
    key: QuickFilterKey;
    icon: any;
    labelKey: string;
    fallback: string;
    params: QuickFilterParams;
}[] = [
    { key: "all", icon: MessagesSquare, labelKey: "roomchat.filter_all", fallback: "Tất cả hội thoại", params: {} },
    { key: "unread", icon: CircleDot, labelKey: "roomchat.filter_unread", fallback: "Chưa đọc", params: { is_unread: 1 } },
    { key: "unanswered", icon: Mail, labelKey: "roomchat.filter_unanswered", fallback: "Chưa trả lời", params: { unanswered: 1 } },
    { key: "unanswered_oldest", icon: MailWarning, labelKey: "roomchat.filter_unanswered_oldest", fallback: "Chưa trả lời - đợi lâu nhất", params: { unanswered: 1, sortRaw: "last_seen_asc" } },
    { key: "read_unanswered", icon: MailOpen, labelKey: "roomchat.filter_read_unanswered", fallback: "Đã đọc nhưng chưa trả lời", params: { read_unanswered: 1 } },
    { key: "priority", icon: Star, labelKey: "roomchat.filter_priority", fallback: "Ưu tiên liên hệ", params: { is_priority_contact: 1 } },
    { key: "comment", icon: MessageCircle, labelKey: "roomchat.filter_comment", fallback: "Bình luận", params: { type: "comment" } },
    { key: "has_phone", icon: Phone, labelKey: "roomchat.filter_has_phone", fallback: "Có số điện thoại", params: { has_phone: 1 } },
    { key: "no_phone", icon: PhoneOff, labelKey: "roomchat.filter_no_phone", fallback: "Chưa có số điện thoại", params: { has_phone: 0 } },
    { key: "unassigned", icon: UserX, labelKey: "roomchat.filter_unassigned", fallback: "Chưa phân công", params: { assignment_status: "unassigned" } },
];

export const getQuickFilterParams = (key: QuickFilterKey): QuickFilterParams =>
    QUICK_FILTERS.find((f) => f.key === key)?.params ?? {};

export default function QuickFilterRail({
    value = "all",
    onChange,
}: {
    value?: QuickFilterKey;
    onChange?: (key: QuickFilterKey) => void;
}) {
    const { t } = useTranslation();

    return (
        <div className="w-[52px] shrink-0 border-r dark:border-[#2a2a2a] bg-white dark:bg-[#1f1f1f] flex flex-col items-center gap-1 py-3 overflow-y-auto">
            {QUICK_FILTERS.map(({ key, icon: Icon, labelKey, fallback }) => {
                const active = value === key;
                return (
                    <Tooltip key={key} title={t(labelKey) || fallback} placement="right">
                        <button
                            onClick={() => onChange?.(key)}
                            className={`
                                w-9 h-9 rounded-lg flex items-center justify-center transition-colors flex-shrink-0
                                ${active
                                    ? "bg-[#3e91ab]/15 text-[#3e91ab]"
                                    : "text-gray-400 hover:text-[#3e91ab] hover:bg-gray-100 dark:hover:bg-[#2a2a2a]"}
                            `}
                        >
                            <Icon size={18} />
                        </button>
                    </Tooltip>
                );
            })}
        </div>
    );
}
